import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Body } from "syf-component-library/ui/typography";
import { Stack } from "syf-component-library/ui/spacing";
import { analyticsConfig } from "syf-js-utilities/helpers/triggerAnalyticsEvent";
import { ModalHeader } from "syf-component-library/ui/patterns/SlideInModalPortal";
import SyfLoader from "../atoms/SyfLoader";
import { useConfig } from "../../hooks/useConfig";
import { PAGE_FUNCTION, PAGE_NAME, PAGE_SUBFUNCTION, } from "../../const/analytics";
import getAccountType, { AccountType } from "../../helpers/getAccountType";
import MainContext from "../Context";
import FeaturedContent from "../molecules/FeaturedContent";
import AccountNotification from "./AccountNotification";
import { AccountInfoFooter, DetailsDrawerHeader, HorizontalLine, } from "./subcomponents";
import isSecuredInstallment from "../../helpers/isSecuredInstallment";
import { useAccountActivity, useAccountSummary, useAutopayStatus, } from "../../helpers/syfApiQueries";
import DataSections from "../molecules/DataSections";
import { INTRO_RATE_LOAN } from "../../const/conditions";
export function renderNotificationBar(_a) {
    var _b;
    var accountActivityData = _a.accountActivityData, accountSummaryData = _a.accountSummaryData, autopayStatusData = _a.autopayStatusData, isNotificationsEnabled = _a.isNotificationsEnabled, isRegistered = _a.isRegistered;
    if (accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.enrollments.includes(INTRO_RATE_LOAN)) {
        return null;
    }
    if (isNotificationsEnabled &&
        autopayStatusData &&
        accountActivityData &&
        accountSummaryData) {
        var accountDetailsObj = {
            activity: accountActivityData === null || accountActivityData === void 0 ? void 0 : accountActivityData.activity,
            billing: accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.billing,
            conditions: accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.conditions,
            credit: accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.credit,
            enrollmentDetails: autopayStatusData.enrollmentDetails,
            installment: accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.installment,
            isRegistered: isRegistered,
            product: (_b = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.product) === null || _b === void 0 ? void 0 : _b.type,
            enrollments: accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.enrollments,
        };
        return (_jsx(AccountNotification, { "data-cy": "notifications", accountDetailsObj: accountDetailsObj }));
    }
    return null;
}
export function isLoadingOrIsFetching(_a) {
    var isAccountActivityLoading = _a.isAccountActivityLoading, isAccountSummaryLoading = _a.isAccountSummaryLoading, isAutopayStatusLoading = _a.isAutopayStatusLoading, isNotificationsEnabled = _a.isNotificationsEnabled;
    if (!isNotificationsEnabled) {
        return isAccountSummaryLoading || isAutopayStatusLoading;
    }
    return (isAccountActivityLoading ||
        isAccountSummaryLoading ||
        isAutopayStatusLoading);
}
var AccountDetails = function (_a) {
    var _b, _c, _d, _e;
    var handleCloseWithAnalytics = _a.handleCloseWithAnalytics;
    var _f = useContext(MainContext), triggerAnalyticsEvent = _f.triggerAnalyticsEvent, featureFlags = _f.featureFlags, isRegistered = _f.isRegistered;
    var config = useConfig();
    var headerRef = useRef();
    var assets = config.hosts.assets;
    var isNotificationsEnabled = featureFlags.isNotificationsEnabled;
    var _g = useAccountSummary(), accountSummaryData = _g.data, isAccountSummaryLoading = _g.isLoading;
    var _h = useAutopayStatus(), autopayStatusData = _h.data, isAutopayStatusLoading = _h.isLoading;
    var _j = useAccountActivity(isNotificationsEnabled), accountActivityData = _j.data, isAccountActivityLoading = _j.isLoading;
    var t = useTranslation().t;
    var productType = (_b = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.product) === null || _b === void 0 ? void 0 : _b.type;
    var clientId = (_c = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.product) === null || _c === void 0 ? void 0 : _c.clientId;
    var accountType = getAccountType(productType);
    var isPN4 = accountType === AccountType.PAY_IN_4;
    var loadingOrFetching = isLoadingOrIsFetching({
        isAutopayStatusLoading: isAutopayStatusLoading,
        isAccountActivityLoading: isAccountActivityLoading,
        isAccountSummaryLoading: isAccountSummaryLoading,
        isNotificationsEnabled: isNotificationsEnabled,
    });
    useEffect(function () {
        if (!triggerAnalyticsEvent)
            return;
        analyticsConfig.defaults.ClientName = String(clientId || "");
        analyticsConfig.defaults.Product = String(productType || "");
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION,
            pageSubFunction: PAGE_SUBFUNCTION,
            pageName: PAGE_NAME.VIEW,
        });
    }, [productType, clientId, triggerAnalyticsEvent]);
    useEffect(function () {
        setTimeout(function () {
            if (headerRef === null || headerRef === void 0 ? void 0 : headerRef.current) {
                headerRef.current.focus();
            }
        }, 10);
    }, [loadingOrFetching, headerRef]);
    if (loadingOrFetching) {
        return _jsx(SyfLoader, {});
    }
    var isSecuredInstallmentLoan = isSecuredInstallment((_d = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.product) === null || _d === void 0 ? void 0 : _d.type);
    var headerLabel = isSecuredInstallmentLoan
        ? t("grid.titles.loanDetails")
        : t("modal.header.text");
    var pastDueAmount = Number((_e = accountSummaryData === null || accountSummaryData === void 0 ? void 0 : accountSummaryData.billing) === null || _e === void 0 ? void 0 : _e.pastDueAmount);
    var isPastDueMoreThanZero = pastDueAmount > 0;
    return (_jsxs(_Fragment, { children: [_jsx(ModalHeader, { headerRef: headerRef, content: headerLabel, "data-cy": "account-details-header-title", closeModal: handleCloseWithAnalytics }), _jsx(Stack, {}), _jsx(DetailsDrawerHeader, { data: { accountSummaryData: accountSummaryData, assets: assets } }), _jsx(Stack, {}), _jsx(HorizontalLine, {}), renderNotificationBar({
                accountActivityData: accountActivityData,
                accountSummaryData: accountSummaryData,
                autopayStatusData: autopayStatusData,
                isNotificationsEnabled: isNotificationsEnabled,
                isRegistered: isRegistered,
            }), _jsx(FeaturedContent, {}), _jsx(DataSections, {}), _jsxs(AccountInfoFooter, { children: [_jsx(Body, { children: isPN4 ? t("grid.accountNoticePN4") : t("grid.accountNotice") }), isSecuredInstallmentLoan && isPastDueMoreThanZero && (_jsxs(_Fragment, { children: [_jsx(Stack, { size: "base" }), _jsxs(Body, { children: [_jsx("sup", { children: "1" }), " ", t("grid.accountNoticeSecuredInstallmentLoan")] })] }))] })] }));
};
export default AccountDetails;
//# sourceMappingURL=AccountDetails.js.map